import {
  TOPIC_CONTENT_SOURCES,
  type TopicContentSource,
} from "./topic-content-sources";
import { TOPIC_GUIDES, type TopicGuide } from "./topic-guides";

export const BANK_SCAM_GUIDE = {
  topicId: "bank-scam",
  locale: "ko",
  publicationStatus: "korean_test_preview",
  risk: "R3",
  checkedAt: "2026-07-16",
  jurisdictionLabel: "호주 전역",
  summary:
    "사기범에게 송금했거나 모르는 카드·계좌 거래를 발견했다면 추가 송금과 연락을 즉시 중단하고, 메시지 속 번호가 아닌 은행 앱·카드 뒷면·공식 웹사이트의 연락처로 은행 또는 카드사 사기팀에 연락하세요. 거래 중지·계좌와 카드 보호·송금 recall 또는 dispute 가능 여부를 요청하고 reference number를 받으세요. 이후 안전한 기기에서 비밀번호·MFA·이메일·통신 계정을 보호하고, Scamwatch와 필요한 경우 ReportCyber에 신고합니다. 환급·거래 회수·은행 책임은 거래 방식과 사실관계에 따라 달라지므로 보장된다고 생각하지 말고, 은행의 정식 complaint 결과에 불만이 있으면 AFCA 절차를 확인하세요.",
  keyPoints: [
    "사기범에게 돈·gift card·crypto·수수료를 더 보내지 말고 모든 연락을 차단하세요.",
    "은행을 사칭한 연락처를 사용하지 말고 공식 앱·카드 뒷면·직접 입력한 공식 웹사이트로 즉시 연락하세요.",
    "은행에 거래 중지, 카드·계좌 보호, transfer recall·charge dispute 가능 여부를 요청하고 reference number를 받으세요.",
    "본인이 승인한 scam payment와 본인 허락 없이 발생한 unauthorised transaction을 사실대로 구분하세요.",
    "비밀번호·인증코드·remote access가 노출됐다면 안전한 기기에서 이메일·은행·통신 계정을 함께 보호하세요.",
    "Scamwatch 신고는 scam intelligence와 차단에 도움을 주지만 개인의 돈을 직접 회수해 주는 서비스는 아닙니다.",
    "신원정보가 노출됐다면 credit report 확인과 무료 credit ban 등 identity-fraud 보호를 검토하세요.",
    "손실 회복을 약속하며 선불 수수료를 요구하는 follow-up 또는 recovery scam을 경계하세요.",
  ],
  sections: [
    {
      id: "stop-and-contact-bank",
      title: "1. 추가 송금을 멈추고 공식 은행 채널에 즉시 연락하세요",
      body: [
        "돈을 보냈다면 더 보내지 말고 scammer와의 전화·문자·메신저·remote-access session을 종료하세요. 은행을 사칭한 메시지의 link나 전화번호를 사용하지 말고, 공식 banking app, 카드 뒷면 또는 직접 입력한 은행 공식 사이트의 fraud·scam contact로 연락하세요. 은행은 password·code 제공, software 설치 또는 ‘safe account’로 송금을 요구하지 않는다는 공식 경고를 기억하세요.",
        "거래 시각·금액·수취인·결제수단을 알리고 pending transaction 중지, card block·replacement, account restriction, transfer recall·trace, charge dispute와 추가 보호조치 가능 여부를 요청하세요. 성공 여부는 보장되지 않으므로 상담자·시간·reference number와 은행이 안내한 다음 단계를 기록하세요. 모르는 거래라면 unauthorised transaction임을 분명히 말하고 추가 거래를 막아 달라고 요청하세요.",
      ],
      checklist: [
        "공식 은행·카드사 fraud contact",
        "거래 날짜·시간·금액·수취인과 transaction ID",
        "은행 이체·카드·gift card·crypto 등 payment method",
        "pending transaction·card·account block 요청",
        "recall·charge dispute·transaction investigation 요청",
        "상담자·시간·reference number와 약속된 회신일",
      ],
      sourceIds: [
        "bank-scam-source-scamwatch-action",
        "bank-scam-source-moneysmart-response",
        "bank-scam-source-unauthorised-transactions",
      ],
    },
    {
      id: "secure-accounts-devices",
      title: "2. 안전한 기기에서 계정·기기·전화번호를 보호하세요",
      body: [
        "scammer가 password, PIN, 인증코드, 신분자료 또는 remote access를 얻었다면 감염되거나 원격제어된 기기에서 바로 banking을 계속하지 마세요. 가능한 경우 안전한 다른 기기에서 모든 sessions를 sign out하고 이메일부터 은행·myGov·super·쇼핑 계정까지 서로 다른 강한 password로 변경하고 MFA를 켜세요. 같은 password를 재사용한 계정도 함께 변경하세요.",
        "remote-access software를 제거하고 device update·security scan을 실행하세요. 전화나 SIM 계정이 노출됐다면 telco에 연락해 SIM-swap·porting protection과 account PIN을 확인하세요. 계좌 activity, email forwarding rule, 새 payee, device registration과 credit application을 계속 관찰하고, 필요하면 신뢰할 수 있는 IT 전문가 또는 Australian Cyber Security Hotline의 안내를 이용하세요.",
      ],
      checklist: [
        "안전한 별도 기기 사용 가능 여부",
        "email·bank·myGov·super의 active sessions 로그아웃",
        "중복 없는 새 password와 MFA",
        "remote-access app·malware·email forwarding rule 점검",
        "telco SIM·porting·account PIN 보호",
        "새 payee·device·transaction·credit enquiry 모니터링",
      ],
      sourceIds: [
        "bank-scam-source-banking-scams",
        "bank-scam-source-moneysmart-response",
        "bank-scam-source-reportcyber",
      ],
    },
    {
      id: "preserve-and-report",
      title: "3. 증거를 보존하고 목적에 맞는 공식 신고를 하세요",
      body: [
        "message·email header·social profile·website URL·전화번호·invoice·bank receipt·wallet address·remote access app·은행 상담기록을 삭제하기 전에 screenshot 또는 export로 보관하세요. 비밀번호나 전체 카드번호를 일반 문서에 복사하지 말고 필요한 증거만 안전하게 정리하세요.",
        "Scamwatch에 scam 유형·접촉수단·손실과 관련 식별정보를 신고하면 National Anti-Scam Centre가 경고·차단·정보 공유에 활용할 수 있지만 Scamwatch가 개인 사건을 조사하거나 환급을 결정하는 기관은 아닙니다. 해킹·계정탈취·online fraud 등 cybercrime은 ReportCyber에서 별도 신고할 수 있습니다. 즉각적인 생명·신체 위험은 000, 긴급하지 않은 범죄 대응은 해당 police·ReportCyber의 현재 안내를 따르세요.",
      ],
      checklist: [
        "원본 message·email·URL·profile과 연락처",
        "transaction receipt·account statement·wallet address",
        "은행·platform·telco complaint reference",
        "Scamwatch report 번호 또는 제출 기록",
        "ReportCyber report와 police reference가 필요한지",
        "증거 파일의 안전한 사본과 사건 timeline",
      ],
      sourceIds: [
        "bank-scam-source-scamwatch-report",
        "bank-scam-source-reportcyber",
      ],
    },
    {
      id: "dispute-and-complain",
      title: "4. 거래 유형을 구분해 은행 dispute와 정식 complaint를 진행하세요",
      body: [
        "본인이 scammer의 지시에 속아 직접 승인한 payment인지, 본인의 허락 없이 발생한 unauthorised transaction인지, 잘못된 계좌로 보낸 mistaken payment인지 사실대로 구분하세요. 적용되는 bank process와 ePayments Code 고려사항이 다를 수 있습니다. 가능한 한 빨리 신고하고 transaction별 설명·증거·원하는 해결을 서면으로 제출하세요. 은행에 recovery·recall·chargeback·refund 가능성을 묻되 결과를 확정적으로 기대하지 마세요.",
        "은행이나 receiving bank의 대응이 부족하다고 생각하면 먼저 해당 financial institution의 internal dispute resolution에 정식 complaint를 제출하고 complaint number, 최종 답변과 이유를 요청하세요. 만족하지 못하면 time limit가 적용될 수 있으므로 AFCA에 제기할 수 있는 complaint 범위와 제출기한을 확인하세요. AFCA 접수도 환급 보장이 아니며 사실·의무·증거를 바탕으로 검토됩니다.",
      ],
      checklist: [
        "authorised scam payment·unauthorised·mistaken transaction 구분",
        "transaction별 사실·금액·시각과 은행에 알린 시각",
        "은행 investigation·recall·dispute 결과",
        "internal complaint number와 written final response",
        "receiving bank 관련 사실과 본인 은행의 안내",
        "AFCA eligibility·time limit·증빙 확인",
      ],
      sourceIds: [
        "bank-scam-source-unauthorised-transactions",
        "bank-scam-source-moneysmart-response",
      ],
    },
    {
      id: "protect-identity-and-recover",
      title: "5. 신원도용·채무·후속 사기를 장기적으로 점검하세요",
      body: [
        "운전면허·여권·Medicare·TFN·주소·생년월일·selfie가 노출됐다면 해당 발급기관과 financial providers에 알리고 replacement·account flag·추가 확인 절차를 문의하세요. 무료 credit report를 확인하고, fraud 피해 또는 위험이 있다면 OAIC 안내에 따라 credit reporting bodies에 consumer credit report ban을 요청할 수 있습니다. 기본 ban period는 21일이며 필요하면 증빙을 갖춰 연장을 요청할 수 있고 신청·연장은 무료입니다.",
        "손실 때문에 rent·loan·bill을 감당하기 어렵다면 creditor hardship team과 무료 financial counsellor에게 조기에 연락하세요. ‘돈을 찾아주겠다’, ‘세금·보험 bond·법률비를 먼저 내라’며 접근하는 recovery scam에 돈이나 신분자료를 다시 보내지 마세요. 은행·경찰·정부를 사칭한 follow-up 연락도 직접 공식 채널로 확인하세요.",
      ],
      sourceIds: [
        "bank-scam-source-moneysmart-response",
        "bank-scam-source-oaic-credit-ban",
      ],
    },
  ],
  faqs: [
    {
      id: "bank-scam-safe-account",
      question: "은행 직원이 돈을 ‘safe account’로 옮기라고 합니다.",
      answer:
        "송금하지 마세요. 전화를 끊고 메시지 속 번호가 아닌 카드 뒷면·공식 앱·직접 입력한 공식 웹사이트의 번호로 은행에 다시 연락하세요. Moneysmart는 은행이 password·code, software 설치 또는 money transfer를 요구하지 않는다고 안내합니다.",
      sourceIds: ["bank-scam-source-banking-scams"],
    },
    {
      id: "bank-scam-transfer-recall",
      question: "이미 bank transfer를 보냈는데 취소할 수 있나요?",
      answer:
        "즉시 은행에 연락해 transaction stop·recall·trace 가능 여부를 요청하세요. 처리 가능성과 회수 결과는 transaction status, receiving account와 은행 절차에 따라 달라지므로 보장되지 않습니다. reference number와 답변을 기록하세요.",
      sourceIds: [
        "bank-scam-source-scamwatch-action",
        "bank-scam-source-moneysmart-response",
      ],
    },
    {
      id: "bank-scam-unknown-card",
      question: "카드에 모르는 결제가 보입니다.",
      answer:
        "merchant의 trading name과 pending 여부를 확인하되 본인 거래가 아니면 즉시 bank·card provider에 unauthorised transaction으로 신고하고 card block·replacement와 추가 거래 중지를 요청하세요. 지연하지 말고 reference number를 받으세요.",
      sourceIds: ["bank-scam-source-unauthorised-transactions"],
    },
    {
      id: "bank-scam-authorised-payment",
      question: "제가 직접 인증하고 송금했어도 은행에 신고해야 하나요?",
      answer:
        "네. scam에 속아 본인이 승인한 payment였다는 사실을 정확히 설명하고 즉시 recovery·recall과 complaint 절차를 요청하세요. unauthorised transaction과 같은 결과가 적용된다고 단정할 수 없으며 환급은 보장되지 않습니다.",
      sourceIds: ["bank-scam-source-moneysmart-response"],
    },
    {
      id: "bank-scam-scamwatch-refund",
      question: "Scamwatch에 신고하면 돈을 돌려받을 수 있나요?",
      answer:
        "Scamwatch 신고는 scam 정보 수집, 경고와 차단에 도움을 주지만 개인의 돈을 직접 회수하거나 refund를 결정하는 서비스는 아닙니다. 돈과 계정 문제는 먼저 bank·payment provider에 즉시 신고하세요.",
      sourceIds: ["bank-scam-source-scamwatch-report"],
    },
    {
      id: "bank-scam-reportcyber",
      question: "Scamwatch와 ReportCyber에 둘 다 신고해야 하나요?",
      answer:
        "목적이 다릅니다. Scamwatch에는 scam 정보를 신고하고, hacking·account takeover·online fraud 같은 cybercrime은 ReportCyber의 현재 category를 확인해 별도 신고할 수 있습니다. 즉각적인 신체 위험은 000입니다.",
      sourceIds: [
        "bank-scam-source-scamwatch-report",
        "bank-scam-source-reportcyber",
      ],
    },
    {
      id: "bank-scam-remote-access",
      question: "scammer가 제 컴퓨터나 휴대폰을 원격으로 조작했습니다.",
      answer:
        "연결을 끊고 그 기기에서 banking을 계속하지 마세요. 안전한 기기에서 sessions·password·MFA를 정리하고 remote-access software 제거와 security scan을 진행하세요. 은행과 telco에도 즉시 알리고 필요한 경우 Cyber Security Hotline 또는 신뢰할 수 있는 IT 지원을 이용하세요.",
      sourceIds: [
        "bank-scam-source-moneysmart-response",
        "bank-scam-source-reportcyber",
      ],
    },
    {
      id: "bank-scam-identity-documents",
      question: "신분증 사진과 개인정보도 보냈습니다.",
      answer:
        "은행·super·telco와 해당 신분증 발급기관에 노출 사실을 알리고 계정 보호·replacement를 문의하세요. credit report를 확인하고 OAIC 안내에 따라 무료 credit ban을 요청할지 검토하세요. ban은 신용신청에 불편을 줄 수 있으므로 조건과 종료·연장 절차를 확인하세요.",
      sourceIds: [
        "bank-scam-source-moneysmart-response",
        "bank-scam-source-oaic-credit-ban",
      ],
    },
    {
      id: "bank-scam-afca",
      question: "은행의 답변에 동의하지 않으면 어떻게 하나요?",
      answer:
        "먼저 은행의 internal dispute resolution에 정식 complaint를 제출하고 complaint number와 written response를 받으세요. 만족하지 못하면 AFCA의 관할·time limit를 확인해 complaint를 제기할 수 있지만 refund나 compensation이 보장되는 것은 아닙니다.",
      sourceIds: [
        "bank-scam-source-moneysmart-response",
        "bank-scam-source-unauthorised-transactions",
      ],
    },
    {
      id: "bank-scam-recovery-fee",
      question: "해외 조사기관이 돈을 회수해준다며 선불 수수료를 요구합니다.",
      answer:
        "돈이나 신분자료를 보내지 마세요. 이미 피해를 본 사람에게 recovery, tax, deposit, retainer 또는 insurance bond 명목의 비용을 요구하는 follow-up scam이 흔합니다. 연락을 차단하고 공식 기관을 직접 확인하세요.",
      sourceIds: ["bank-scam-source-moneysmart-response"],
    },
  ],
  sourceIds: [
    "bank-scam-source-scamwatch-action",
    "bank-scam-source-scamwatch-report",
    "bank-scam-source-banking-scams",
    "bank-scam-source-moneysmart-response",
    "bank-scam-source-unauthorised-transactions",
    "bank-scam-source-reportcyber",
    "bank-scam-source-oaic-credit-ban",
  ],
  additionalChecks: [
    "은행·카드사·payment platform의 current fraud contact와 dispute·chargeback·recall 조건을 즉시 확인하세요.",
    "거래가 pending인지 completed인지, 본인 승인 여부와 신고 시각을 은행 기록에서 재확인하세요.",
    "은행 internal complaint와 AFCA에는 time limit가 있을 수 있으므로 current rules를 제출 전에 확인하세요.",
    "신분증·TFN·Medicare·licence가 노출됐다면 각 발급기관의 current replacement·protection 절차를 확인하세요.",
    "credit ban의 21일 기본기간, 연장 증빙과 신용신청 영향은 OAIC·credit reporting body에서 확인하세요.",
    "즉각적인 생명·신체 위험은 000을 이용하고, 심각한 정서적 위기에는 현재 crisis support를 이용하세요.",
  ],
  disclaimer:
    "이 콘텐츠는 호주에서 bank scam, unauthorised transaction, account takeover와 identity exposure에 대응하기 위한 R3 테스트 미리보기 생활정보이며 법률·금융 자문이 아닙니다. 은행의 거래 회수·refund·chargeback·compensation, AFCA 결과, police 조사 또는 credit-ban 필요성을 개인에게 확정하지 않습니다. 거래방식, 신고시점, 약관, ePayments Code 적용과 증거에 따라 결과가 달라지므로 은행·payment provider·Scamwatch·ReportCyber·OAIC의 최신 공식 안내를 즉시 확인하세요.",
} as const satisfies TopicGuide;

export const BANK_SCAM_SOURCES = [
  {
    sourceId: "bank-scam-source-scamwatch-action",
    topicId: "bank-scam",
    title: "What to do if you've been scammed",
    publisher: "Scamwatch",
    url: "https://www.scamwatch.gov.au/what-to-do-if-youve-been-scammed",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Scamwatch 공식 페이지 본문에서 추가 송금 중단, bank·card provider 즉시 연락, IDCARE·AFCA·ReportCyber와 recovery-scam 경고 확인",
    reviewStatus: "live_verified",
    claimScope: "scam 피해 직후 payment·identity·support·complaint 대응의 공통 우선순위",
    followUp: "개별 payment method와 신고기관의 current contact·recovery 절차 재확인",
  },
  {
    sourceId: "bank-scam-source-scamwatch-report",
    topicId: "bank-scam",
    title: "Report a scam",
    publisher: "Scamwatch",
    url: "https://www.scamwatch.gov.au/report-a-scam",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Scamwatch 공식 신고 페이지에서 bank 우선 연락, Scamwatch 신고 목적, cybercrime·police·000 경로 확인",
    reviewStatus: "live_verified",
    claimScope: "Scamwatch report의 목적·한계와 별도 긴급·cybercrime 신고 경로",
    followUp: "현재 report form의 privacy·정보공유·category와 긴급상황 channel 재확인",
  },
  {
    sourceId: "bank-scam-source-banking-scams",
    topicId: "bank-scam",
    title: "Banking scams",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/financial-scams/banking-scams",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Australian Government Moneysmart 본문에서 bank impersonation·safe account·remote access 경고와 password·MFA 보호 확인",
    reviewStatus: "live_verified",
    claimScope: "banking scam 식별, official bank channel 재확인과 기본 보안조치",
    followUp: "은행별 current scam alert와 official contact를 직접 확인",
  },
  {
    sourceId: "bank-scam-source-moneysmart-response",
    topicId: "bank-scam",
    title: "What to do if you've been scammed",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/check-and-report-scams/what-to-do-if-youve-been-scammed",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Moneysmart 본문에서 bank 즉시 연락, payment별 대응, account·device 보호, follow-up scam과 AFCA escalation 확인",
    reviewStatus: "live_verified",
    claimScope: "scam payment·personal information·device compromise·complaint와 support의 행동 순서",
    followUp: "bank·AFCA·IDCARE·financial counsellor의 current eligibility와 contact 재확인",
  },
  {
    sourceId: "bank-scam-source-unauthorised-transactions",
    topicId: "bank-scam",
    title: "Unauthorised and mistaken transactions",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/banking/unauthorised-and-mistaken-transactions",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Moneysmart 본문에서 unauthorised·mistaken 정의, 즉시 bank 신고, reference number, ePayments Code와 AFCA complaint 확인",
    reviewStatus: "live_verified",
    claimScope: "personal account의 unauthorised·mistaken transaction 식별과 bank dispute·complaint 기본 절차",
    followUp: "개별 bank의 ePayments Code 가입, transaction type, time limit와 증거 요구 재확인",
  },
  {
    sourceId: "bank-scam-source-reportcyber",
    topicId: "bank-scam",
    title: "Report a cybercrime, incident or vulnerability",
    publisher: "Australian Signals Directorate's Australian Cyber Security Centre",
    url: "https://www.cyber.gov.au/report-and-recover/report",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Cyber.gov.au 공식 Report 페이지에서 ReportCyber submission, saved report status, 000 emergency와 national policing initiative 확인",
    reviewStatus: "live_verified",
    claimScope: "hacking·account takeover·online fraud 등 cybercrime 신고와 긴급상황 구분",
    followUp: "사건 category별 ReportCyber form, police referral와 Cyber Security Hotline 이용요건 재확인",
  },
  {
    sourceId: "bank-scam-source-oaic-credit-ban",
    topicId: "bank-scam",
    title: "Fraud and your credit report",
    publisher: "Office of the Australian Information Commissioner",
    url: "https://www.oaic.gov.au/privacy/your-privacy-rights/credit-reporting/fraud-and-your-credit-report",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "OAIC 공식 본문에서 fraud 위험 시 3 credit reporting bodies의 free ban, 21일 기본기간, 연장·해제와 credit application 영향 확인",
    reviewStatus: "live_verified",
    claimScope: "identity fraud 피해·위험 시 consumer credit report ban의 권리와 기본 절차",
    followUp: "신청 시점의 credit reporting body forms, evidence와 ban extension 조건 재확인",
  },
] as const satisfies readonly TopicContentSource[];

const mutableGuides = TOPIC_GUIDES as unknown as TopicGuide[];
if (!mutableGuides.some((guide) => guide.topicId === BANK_SCAM_GUIDE.topicId)) {
  mutableGuides.push(BANK_SCAM_GUIDE);
}

const mutableSources = TOPIC_CONTENT_SOURCES as unknown as TopicContentSource[];
for (const source of BANK_SCAM_SOURCES) {
  if (!mutableSources.some((existing) => existing.sourceId === source.sourceId)) {
    mutableSources.push(source);
  }
}
